import {
  currencyChipHtml, dateFieldHtml, escapeHtml, formatAmount, markCurrentRow, mountDateField,
  notchedFieldHtml, type MoneyCurrency,
} from '@colplat/ui-web';
import { appHref } from '../globals.js';
import { accountLabelHtml, inlineAccountIcon } from './webledgerAccountDisplay.js';
import { readCached } from '../offlineReads.js';
import { datasetLedger } from './webledgerLedger.js';
import {
  generalLedger, selectTransactions, transactionCurrenciesInUse, trialBalance,
  type ReportFilters,
  bankInfoOf,
} from '@colplat/app-webledger/domain';
import { amountLabel } from './webledgerLabels.js';
import { PREF } from './webledgerPrefs.js';
import { fillTotalModes, totalsHtml } from './webledgerTotals.js';
import { restoreScroll, trackScroll, type ScrollTracker } from './webledgerScroll.js';
import { recordState, RecordStateTracker } from './webledgerRecordState.js';
import type { AccountDto, CurrencyDto, TransactionDto } from './webledgerOfflineState.js';
import {
  generalLedgerGroups, groupByDate, runningBalances, trialBalanceGroups,
  type FxRates, type GeneralLedgerRowDto, type ReportSplitDto, type ReportTransactionDto,
  type RunningBalances, type TrialBalanceRowDto,
} from './webledgerReportsData.js';
import {
  bsModal, openTransactionEditor, reopenDiscardedEditor, transactionEditorModalHtml, wireSplitHelp,
} from './webledgerTransactionEditor.js';
import { apiFetch } from '../api.js';
import { bankInfoBadgeHtml, mountBankInfo } from './webledgerBankInfo.js';
import { SHOW_PURPOSES } from './webledgerFeatures.js';
import { loadTags, type TagDto } from './webledgerTags.js';
import { bootstrapWebledgerClient, webledgerShellHtml, wireBackButton, type WebledgerClient } from '../webledgerClient.js';
import { wireHeadReveal } from './webledgerHeadReveal.js';

/**
 * WebLedger's reports page — legacy's `reports.php` / `assets/reports.js`.
 *
 * A picker over three reports that share only a date range: the filtered
 * register ("All transactions"), a trial balance, and a general ledger. The
 * currency, purpose and folder filters apply to the first one only, and the
 * page hides them for the other two exactly as legacy does — they would be
 * inert there, and an inert control that looks live is worse than an absent
 * one.
 *
 * Cards are editable, as legacy's are: an Edit button and a double-click both
 * open the transaction. They open the *same* editor the register uses
 * (`webledgerTransactionEditor.ts`), not a reduced copy — leases, presence,
 * live previews, take-over, held drafts, offline queueing and the change
 * timeline all arrive with it. A second editor over the same document type
 * would eventually become a second, subtly different set of rules about who
 * may write, which is the one kind of divergence this app cannot afford. It
 * is also why the editor is a module rather than a private part of the
 * register: "the reports need it too" was always going to happen.
 */

type ReportKind = 'all' | 'trial-balance' | 'general-ledger';

interface PurposeDto { id: string; name: string }



/**
 * A report row as the record the editor edits.
 *
 * Lossless, which is why the report endpoints carry `version` on the
 * transaction and on every leg: without it, opening a row would mean
 * re-reading it first, and a report held in the offline cache would have
 * nothing openable in it at all. The extra fields a report row carries
 * (labels, paths, scope flags) are display-only and simply do not travel.
 */
function editableTransaction(row: ReportTransactionDto): TransactionDto {
  return {
    id: row.id, txDate: row.txDate, description: row.description, reference: row.reference,
    currencyCode: row.currencyCode, version: row.version,
    splits: row.splits.map((s) => ({
      id: s.id, transactionId: row.id, accountId: s.accountId, memo: s.memo,
      value: s.value, amount: s.amount, currencyCode: s.currencyCode, version: s.version,
    })),
  };
}

interface AllTransactionsResponse {
  transactions: ReportTransactionDto[];
  currencies: string[];
  purposes: PurposeDto[];
  /** Which of these came from a bank feed. Ids only; the payload is fetched on demand. */
  bankInfo?: string[];
  applied: { from?: string; to?: string; currency?: string; purpose?: string; folder?: string };
}

export async function mountWebledgerReports(root: HTMLElement): Promise<void> {
  root.innerHTML = webledgerShellHtml('reports', shellBodyHtml())
    + transactionEditorModalHtml()
    + '<div id="wlTimeline"></div>'
    + '<div class="cf-sr" aria-live="polite" aria-atomic="true"></div>';
  wireBackButton(root);
  // The page's bar stays at the top as the small header; a tap on it brings
  // the whole header back (webledgerHeadReveal).
  wireHeadReveal(root.querySelector<HTMLElement>('#wlReportFilters')!, { styled: true });
  wireSplitHelp(root);

  const client = await bootstrapWebledgerClient(root);
  if (!client) return;
  const { token, docCache, prefs, dataset } = client;

  const areaEl = root.querySelector<HTMLElement>('#wlReportArea')!;
  // Delegated from the report area, so a re-run replaces the cards without
  // leaving a stale panel or a dead listener behind.
  mountBankInfo(areaEl, token, {
    localEntries: (txId) => bankInfoOf(datasetLedger(client.dataset), txId),
  });
  const formEl = root.querySelector<HTMLFormElement>('#wlReportFilters')!;
  const kindSelect = formEl.elements.namedItem('report') as HTMLSelectElement;
  const fromInput = formEl.elements.namedItem('from') as HTMLInputElement;
  const toInput = formEl.elements.namedItem('to') as HTMLInputElement;
  const currencySelect = formEl.elements.namedItem('currency') as HTMLSelectElement;
  const purposeSelect = formEl.elements.namedItem('purpose') as HTMLSelectElement;
  const folderSelect = formEl.elements.namedItem('folder') as HTMLSelectElement;
  const tagRowEl = root.querySelector<HTMLElement>('#wlFilterTags')!;
  const tagChipsEl = root.querySelector<HTMLElement>('#wlTagChips')!;
  const tagModeWrapEl = root.querySelector<HTMLElement>('#wlTagModeWrap')!;
  const tagModeSelect = root.querySelector<HTMLSelectElement>('#wlTagMode')!;
  const tagHelpEl = root.querySelector<HTMLElement>('#wlTagHelp')!;
  /** The chosen tags, in the order the manager lists them — not click order, which would jump about. */
  const selectedTags = new Set<string>();
  let allTags: TagDto[] = [];

  /**
   * The tag chips, and the two controls that only make sense once something
   * is chosen.
   *
   * `Match any/all` appears only with a second tag selected, because with one
   * tag the two answers are identical and offering a choice that changes
   * nothing is worse than offering none. The explanation of what the totals
   * count appears with the first, because that is when the numbers start
   * meaning something different from every other report.
   */
  function renderTagFilter(): void {
    tagRowEl.hidden = allTags.length === 0;
    tagChipsEl.innerHTML = allTags
      .map((tag) => {
        const on = selectedTags.has(tag.id);
        return `<button type="button" class="wl-tag wl-tag--pick${on ? ' is-on' : ''}"
          data-tag-pick="${escapeHtml(tag.id)}" aria-pressed="${on}">${escapeHtml(tag.name)}</button>`;
      })
      .join('');
    tagModeWrapEl.hidden = selectedTags.size < 2;
    tagHelpEl.hidden = selectedTags.size === 0;
  }

  tagChipsEl.addEventListener('click', (event) => {
    const chip = (event.target as HTMLElement).closest<HTMLElement>('[data-tag-pick]');
    if (!chip) return;
    const id = chip.dataset['tagPick']!;
    if (selectedTags.has(id)) selectedTags.delete(id); else selectedTags.add(id);
    renderTagFilter();
    prefs.set(PREF.reportTags, [...selectedTags].join(','));
    void run();
  });
  tagModeSelect.addEventListener('change', () => {
    prefs.set(PREF.reportTagMode, tagModeSelect.value);
    void run();
  });
  const staleEl = root.querySelector<HTMLElement>('#wlReportStale')!;

  // Reference data the reports format with. Cached like every other read, so
  // a report already run on this device still renders offline.
  const [accountsRead, currenciesRead] = await Promise.all([
    readCached<{ accounts: AccountDto[] }>(docCache, 'accounts', '/apps/webledger/accounts', token),
    readCached<{ currencies: CurrencyDto[] }>(docCache, 'currencies', '/apps/webledger/currencies', token),
  ]);
  if (!accountsRead.ok && accountsRead.status === 403) {
    areaEl.innerHTML = '<p class="text-secondary">You do not have access to this app.</p>';
    return;
  }
  const accounts = accountsRead.ok ? accountsRead.data.accounts : [];
  const currencies = currenciesRead.ok ? currenciesRead.data.currencies : [];
  const currencyByCode = new Map(currencies.map((c) => [c.code, c] as const));
  const money = (code: string): MoneyCurrency | undefined => currencyByCode.get(code);
  /**
   * Both aggregate reports identify an account by its leaf name, as legacy
   * does — and legacy orders by that name too, so the grouping and the label
   * agree. That leaves two accounts sharing a leaf name indistinguishable;
   * the full path on hover resolves it without widening every row for the
   * common case where the leaf name is unambiguous.
   */
  const pathOf = (id: string): string => accountPath(accounts, id);
  const iconOf = (id: string): string => inlineAccountIcon(accounts, id);
  /** The transactions currently rendered, so a card click can find its record. */
  let shown: ReportTransactionDto[] = [];
  /**
   * The same lease/marker tracker the register uses. Cards are editable, so
   * they owe the reader the same warning a row does — and a second tracker
   * would be a second set of the subtleties its module comment lists.
   */
  const records = new RecordStateTracker(client, () => paintCardStates());
  /** The transaction card carrying the marker — see `markCurrentRow`. */
  let currentTxId: string | null = null;
  /** Only the first run restores a position; later ones are the reader's own doing. */
  let firstRun = true;
  /**
   * The currency and purpose filters as they were last left, applied to the
   * first run and then forgotten.
   *
   * Held here rather than assigned to the selects, because the selects cannot
   * hold them yet: their options are built from what the server reports is
   * actually in use, so assigning a value to an empty select yields `''`
   * silently. After the first response the selects *are* the truth — the
   * server echoes back what it applied, which is not always what was asked
   * for (a purpose can have been deleted since).
   */
  let restoredFilters: { currency: string; purpose: string } | undefined = {
    currency: prefs.get(PREF.reportCurrency) ?? '',
    // Emptied while purposes are hidden. A filter somebody chose before the
    // switch would otherwise go on narrowing every report, with no control
    // on screen saying so and no way to clear it.
    purpose: SHOW_PURPOSES ? (prefs.get(PREF.reportPurpose) ?? '') : '',
  };
  // Listening from here, recording only once the first report has arrived.
  // Gated: all three reports scroll the same window, and a position taken
  // from a trial balance means nothing in a 400-card transaction list.
  const scrollTracker: ScrollTracker =
    trackScroll(prefs, PREF.scrollReports, () => kindSelect.value === 'all');
  /** The per-currency totals of what is on screen, so the bar can be redrawn without re-running. */
  let shownTotals: Record<string, string> = {};

  /**
   * Rates are read like any other reference data — cached, so a report opened
   * offline still converts, with the staleness the server reported carried
   * through rather than hidden.
   */
  const fxRead = await readCached<FxRates>(docCache, 'fx', '/apps/webledger/fx', token);
  const fx: FxRates | undefined = fxRead.ok ? fxRead.data : undefined;

  // Only placeholder accounts can be folders, matching legacy's folder view:
  // a folder is a grouping node, and scoping to a leaf would just be that
  // account's own register, which the Accounts page already offers.
  folderSelect.innerHTML = '<option value="">Whole ledger</option>'
    + accounts
      .filter((a) => a.isPlaceholder)
      .map((a) => `<option value="${escapeHtml(a.id)}">${escapeHtml(accountPath(accounts, a.id))}</option>`)
      .join('');

  function syncFilterVisibility(): void {
    const isAll = kindSelect.value === 'all';
    for (const id of ['wlFilterCurrency', 'wlFilterPurpose', 'wlFilterFolder']) {
      root.querySelector<HTMLElement>(`#${id}`)!.hidden = !isAll;
    }
  }


  /**
   * The two things about a transaction a device cannot know: who is holding it
   * and whether it came from a bank feed.
   *
   * Best-effort and fire-and-forget. Offline it simply does not arrive, and
   * the report draws without lock marks — which is the honest degradation,
   * because the alternative is a report that will not draw at all for want of
   * information it does not need in order to be correct.
   */
  async function attachTransactionState(rows: ReportTransactionDto[]): Promise<void> {
    if (rows.length === 0) return;
    const res = await apiFetch('/apps/webledger/transactions/state', {
      token, method: 'POST', body: { ids: rows.map((t) => t.id) },
    }).catch(() => undefined);
    if (!res?.ok) return;
    const body = (await res.json()) as {
      locks: Array<{ id: string; lockedBy?: unknown; ownedBy?: unknown; ownedSince?: string | null }>;
      bankInfo: string[];
    };
    const byRow = new Map(rows.map((t) => [t.id, t]));
    for (const lock of body.locks) {
      const row = byRow.get(lock.id) as Record<string, unknown> | undefined;
      if (!row) continue;
      row['lockedBy'] = lock.lockedBy;
      row['ownedBy'] = lock.ownedBy;
      row['ownedSince'] = lock.ownedSince;
    }
    records.seed(body.locks as never);
    /*
     * Re-render only if the badges would actually change.
     *
     * The cards are already on screen by the time this resolves — that is the
     * point, so that no report waits on a request it does not need to be
     * correct. A second render is cheap; an unconditional one would throw
     * away the reader's place every time a report ran.
     */
    const fresh = new Set(body.bankInfo);
    const changed = fresh.size !== bankIds.size || [...fresh].some((id) => !bankIds.has(id));
    bankIds = fresh;
    if (changed) repaintCards();
    paintCardStates();
  }

  /** The ids of the rows that came from a bank feed, as last learned. */
  let bankIds = new Set<string>();
  /** Set by `run`, so the late-arriving metadata can redraw the same cards. */
  let repaintCards: () => void = () => {};

  /** The filter bar, as the domain reads it — no query string in between. */
  function currentFilters(): ReportFilters {
    const kind = kindSelect.value as ReportKind;
    const base: ReportFilters = {
      ...(fromInput.value ? { from: fromInput.value } : {}),
      ...(toInput.value ? { to: toInput.value } : {}),
    };
    if (kind !== 'all') return base;
    const currency = restoredFilters ? restoredFilters.currency : currencySelect.value;
    const purpose = restoredFilters ? restoredFilters.purpose : purposeSelect.value;
    return {
      ...base,
      ...(currency ? { currency } : {}),
      ...(purpose ? { purpose } : {}),
      ...(folderSelect.value ? { folder: folderSelect.value } : {}),
      ...(selectedTags.size > 0
        ? { tags: [...selectedTags], tagMode: tagModeSelect.value === 'all' ? 'all' as const : 'any' as const }
        : {}),
    };
  }

  /*
   * Every report is computed here now (ADR-0025).
   *
   * This is the half of the offline requirement that was genuinely missing.
   * The cache used to be keyed by the whole query string, so a report was
   * replayed offline if it had been run online first and returned nothing
   * otherwise — which the page said out loud. Now the filters are the same
   * functions `services/api` runs, over the local replica, so a combination
   * nobody has ever tried answers exactly as well with no connection as with
   * one.
   *
   * What is still fetched is what cannot be computed: who holds a lease, and
   * which rows came from a bank feed. Both are best-effort — a report that
   * refused to draw because nobody could be asked who is editing would be a
   * worse answer than a report without lock marks.
   */
  async function run(): Promise<void> {
    syncFilterVisibility();
    const kind = kindSelect.value as ReportKind;
    areaEl.innerHTML = '<p class="text-secondary" role="status">Running…</p>';
    // Emptying a 250-card list collapses the page and clamps the window to
    // the top. That is not a scroll the reader made, and saving it would
    // throw away the position they are about to come back to.
    scrollTracker.settling();
    staleEl.hidden = true;

    const ledger = datasetLedger(dataset);
    const filters = currentFilters();

    if (kind === 'trial-balance') {
      areaEl.innerHTML = renderTrialBalance(
        trialBalance(ledger, filters), money, pathOf, iconOf);
      firstRun = false;
      return;
    }
    if (kind === 'general-ledger') {
      areaEl.innerHTML = renderGeneralLedger(
        generalLedger(ledger, filters), money, pathOf, iconOf);
      firstRun = false;
      return;
    }

    const transactions = selectTransactions(ledger, filters) as unknown as ReportTransactionDto[];
    const data: AllTransactionsResponse = {
      transactions,
      currencies: transactionCurrenciesInUse(ledger),
      purposes: ledger.purposes().map((p) => ({ id: p.id, name: p.name })),
      // Echoed back from what was actually applied, with anything that no
      // longer exists dropped — the same contract the server had, so the
      // filter bar never claims a filter the rows do not have.
      applied: {
        ...(filters.from ? { from: filters.from } : {}),
        ...(filters.to ? { to: filters.to } : {}),
        ...(filters.currency && transactionCurrenciesInUse(ledger).includes(filters.currency)
          ? { currency: filters.currency } : {}),
        ...(filters.purpose && ledger.purposes().some((p) => p.id === filters.purpose)
          ? { purpose: filters.purpose } : {}),
        ...(filters.folder ? { folder: filters.folder } : {}),
      },
    };
    void attachTransactionState(transactions);
    // Repopulate the pickers from what the server says is actually in use,
    // then re-select what it confirms it applied — not what we asked for.
    // The two differ whenever a filter value has gone away, and echoing the
    // request back would leave the bar claiming a filter the rows do not have.
    fillSelect(currencySelect, 'All currencies',
      data.currencies.map((c) => ({ value: c, label: c })), data.applied.currency ?? '');
    // Tags arrive with the rest of the filter vocabulary. The saved
    // selection is applied only to ids that still exist, so a deleted tag
    // drops out of the filter instead of silently narrowing every future
    // report to nothing.
    allTags = await loadTags(token);
    const known = new Set(allTags.map((t) => t.id));
    for (const id of (prefs.get(PREF.reportTags) ?? '').split(',').filter(Boolean)) {
      if (known.has(id)) selectedTags.add(id);
    }
    if (prefs.get(PREF.reportTagMode) === 'all') tagModeSelect.value = 'all';
    renderTagFilter();

    fillSelect(purposeSelect, 'All purposes',
      data.purposes.map((p) => ({ value: p.id, label: p.name })), data.applied.purpose ?? '');
    folderSelect.value = data.applied.folder ?? '';
    // Remembered from what the server confirms it *applied*, not from what
    // was asked for. A filter whose value has gone away clears itself here
    // rather than being reapplied every visit to a report it can no longer
    // narrow.
    restoredFilters = undefined;
    prefs.set(PREF.reportCurrency, data.applied.currency ?? '');
    prefs.set(PREF.reportPurpose, data.applied.purpose ?? '');
    paintCards(data.transactions);
    // The select is part of the bar, so it is rebuilt with it — fill it here
    // rather than once at mount, when it does not exist yet.
    fillTotalModes(root.querySelector<HTMLSelectElement>('#wlTotalMode'), fx, prefs.get(PREF.reportTotalMode) ?? 'by');
    paintTotals();
    paintCardStates();
    // Re-applied on every run, because every run rebuilds the cards.
    markCurrentRow(areaEl, currentTxId, { attr: 'data-tx-card' });
    // Only on the first: re-running a report is a deliberate act with a new
    // answer to read from the top, not a return to where you were.
    if (firstRun) {
      firstRun = false;
      await restoreScroll(prefs, PREF.scrollReports);
    }
  }

  /**
   * The cards, the running column and the totals, from one accumulation.
   *
   * Called by `run` and by the live redraw below, so the two cannot drift
   * apart — which matters because the live one deliberately does far less
   * than `run` does, and the part they share is exactly this.
   */
  function paintCards(transactions: ReportTransactionDto[]): void {
    shown = transactions;
    records.seed(transactions);
    records.subscribe(transactions.map((t) => t.id));
    // Computed once and shared: the Running column and the totals bar are the
    // same accumulation read at different points.
    const balances = runningBalances(transactions);
    shownTotals = balances.totals;
    repaintCards = (): void => {
      areaEl.innerHTML = renderAllTransactions(transactions, money, balances, iconOf, bankIds);
      markCurrentRow(areaEl, currentTxId, { attr: 'data-tx-card' });
    };
    repaintCards();
  }

  /**
   * Somebody changed something: recompute what is on screen, and nothing
   * else.
   *
   * Emphatically not `run`. Running a report is a deliberate act — it empties
   * the page, says "Running…", rebuilds every filter select from the
   * vocabulary the answer turned out to use, re-reads the tag list over the
   * network, writes the chosen filters back to preferences and scrolls to the
   * top. Every one of those is right when a person pressed the button and
   * wrong when a colleague saved a transaction three rooms away: it would
   * blank the report they are reading, throw away their place, and close
   * whichever select they had open (invariant #5).
   *
   * So this re-asks the same question of the newer rows and redraws the
   * answer. The filter bar is left exactly as the reader set it — a filter
   * value that has since been deleted is dropped on the next real run, which
   * is where that decision already lived.
   */
  function refresh(): void {
    const kind = kindSelect.value as ReportKind;
    const ledger = datasetLedger(dataset);
    const filters = currentFilters();
    if (kind === 'trial-balance') {
      areaEl.innerHTML = renderTrialBalance(trialBalance(ledger, filters), money, pathOf, iconOf);
      return;
    }
    if (kind === 'general-ledger') {
      areaEl.innerHTML = renderGeneralLedger(generalLedger(ledger, filters), money, pathOf, iconOf);
      return;
    }
    const transactions = selectTransactions(ledger, filters) as unknown as ReportTransactionDto[];
    paintCards(transactions);
    paintTotals();
    paintCardStates();
    // The row set may have grown, and a new row has no lock state until
    // somebody asks. Best-effort, after the paint, as on the first run.
    void attachTransactionState(transactions);
  }

  /*
   * Redraw whenever the rows move.
   *
   * `revision` rather than the bare notification: most deltas carry no rows
   * and notify anyway, and a general ledger is an accumulation over the whole
   * book. Running it several times a minute for no change is the one thing
   * this page cannot afford to do quietly.
   */
  let drawnAt = -1;
  dataset.subscribe(() => {
    if (dataset.revision() === drawnAt) return;
    drawnAt = dataset.revision();
    // Nothing has been drawn yet — the first `run` is still in flight and
    // owns the page. Redrawing underneath it would race it.
    if (firstRun) return;
    refresh();
  });

  /**
   * The totals bar, redrawn on its own when the mode changes.
   *
   * Separate from the report render because switching "By currency" to
   * "In EUR" changes one line and nothing else — re-running the report to
   * recompute a sum we already hold would throw away the scroll position
   * over a display choice.
   */
  function paintTotals(): void {
    const box = root.querySelector<HTMLElement>('#wlTotalsBody');
    const select = root.querySelector<HTMLSelectElement>('#wlTotalMode');
    if (!box || !select) return;
    const mode = select.value;
    box.innerHTML = totalsHtml(shownTotals, mode, fx, (amount, code) => chip(amount, code, money));
  }

  /**
   * Repaint just the state chrome on each card.
   *
   * Separate from rendering the report because a lease appearing changes one
   * border and one chip — re-running the whole report to show it would throw
   * away the reader's scroll position every time a colleague opened a
   * transaction, which on a busy ledger is constantly.
   */
  function paintCardStates(): void {
    for (const card of areaEl.querySelectorAll<HTMLElement>('[data-tx-card]')) {
      const id = card.dataset['txCard']!;
      const holder = records.lockedBy.get(id);
      const state = recordState({
        tx: { id }, holder, owner: holder ? undefined : records.ownedBy.get(id),
        held: undefined, queuedEdit: false,
      });
      card.className = `card wl-tx-card mb-2${state.cls ? ` ${state.cls}` : ''}`;
      if (state.color) card.style.setProperty('--row-color', state.color);
      else card.style.removeProperty('--row-color');
      const slot = card.querySelector<HTMLElement>('[data-state-slot]');
      if (slot) slot.innerHTML = state.chip;
    }
  }

  /**
   * Re-run after a save rather than patching the edited row back in.
   *
   * Legacy patches (`runKeepingPlace` + `tx_row`) because its report is the
   * page you work in all day and a full reload loses your place. Here every
   * running balance below the edited row changes with it, and so does every
   * total, so a patch means recomputing the whole set anyway — the same work
   * with one more way to be wrong. Worth revisiting if the reload becomes
   * noticeable; the row builder already supports the single-row read.
   */
  const afterSave = (savedId: string): void => {
    // Point back at what was just edited: `run()` rebuilds every card, so
    // without this the reader is returned to a list with no sign of where
    // they had been working.
    currentTxId = savedId;
    void run().then(() => markCurrentRow(areaEl, savedId, { attr: 'data-tx-card', flash: true, scroll: 'if-needed' }));
  };

  function openRow(id: string): void {
    const row = shown.find((t) => t.id === id);
    if (!row) return;
    void openTransactionEditor(root, client!, editableTransaction(row), accounts, currencies, afterSave);
  }

  // An editor this tab had open when the browser discarded it, back with its
  // unsaved values (webledgerFormRestore.ts).
  void reopenDiscardedEditor(root, client, accounts, currencies, afterSave);

  // Delegated: the select is replaced every time the report re-renders, so a
  // handler bound to the element itself would survive exactly one run.
  areaEl.addEventListener('change', (event) => {
    const select = (event.target as HTMLElement).closest<HTMLSelectElement>('#wlTotalMode');
    if (!select) return;
    prefs.set(PREF.reportTotalMode, select.value);
    paintTotals();
  });

  formEl.addEventListener('submit', (event) => { event.preventDefault(); void run(); });
  // Our own date control rather than the browser's — see dateField.ts.
  for (const id of ['wlReportFrom', 'wlReportTo']) {
    mountDateField(
      root.querySelector<HTMLInputElement>(`#${id}`)!,
      root.querySelector<HTMLElement>(`[data-date-open="${id}"]`)!,
    );
  }
  // Changing which report you are looking at re-runs immediately; changing a
  // filter waits for Run, as legacy does. The difference is deliberate: the
  // report select is the page's subject, the filters are a query you build up.
  kindSelect.addEventListener('change', () => { void run(); });
  areaEl.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    // Clicking a card moves the marker to it — same gesture as the chart of
    // accounts and the register, so "where was I" means one thing across the
    // app. A currency chip is its own gesture.
    if (!target.closest('.cf-cur-chip')) {
      const card = target.closest<HTMLElement>('[data-tx-card]');
      if (card) {
        currentTxId = card.dataset['txCard'] ?? null;
        markCurrentRow(areaEl, currentTxId, { attr: 'data-tx-card' });
      }
    }
    const button = target.closest<HTMLElement>('[data-open-tx]');
    if (button) openRow(button.dataset['openTx']!);
  });
  // Double-click anywhere on a card opens it too, as legacy has it — except
  // on the controls inside it, where a double-click means what they mean.
  areaEl.addEventListener('dblclick', (event) => {
    const target = event.target as HTMLElement;
    if (target.closest('a, button, input, select')) return;
    const card = target.closest<HTMLElement>('[data-tx-card]');
    if (card) openRow(card.dataset['txCard']!);
  });

  client.transport.onMessage((msg) => records.handleMessage(msg));
  client.transport.onStateChange((state) => {
    // The fan-out registry is per-connection, so a new socket knows nothing
    // about what this page was watching.
    records.resetSubscriptions();
    if (state === 'open') records.subscribe(shown.map((t) => t.id));
  });

  wireDeleteAll(root, client, () => { firstRun = true; void run(); });

  syncFilterVisibility();
  await run();
  scrollTracker.start();
}

/** The phrase the server also checks. Duplicated deliberately — the field has to *say* what to type. */
const DELETE_ALL_PHRASE = 'DELETE ALL TRANSACTIONS';

function deleteAllModalHtml(): string {
  return `
    <div class="modal fade" id="wlDeleteAllModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header py-2">
            <div class="fw-semibold">Delete all transactions</div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form id="wlDeleteAllForm">
            <div class="modal-body">
              <p class="small mb-2">
                This removes <strong>every transaction in the ledger</strong> and every leg belonging to one.
                Accounts, currencies, purposes and templates are kept.
              </p>
              <p class="small mb-2">
                Deletion is <strong>soft</strong>: the rows stay in the database with a deletion event against
                each, and they can be put back from "Deleted records". It is not the same as gone.
              </p>
              <p class="small mb-3">
                Bank-imported transactions stay de-duplicated, so a later sync will
                <strong>not</strong> bring them back.
              </p>
              <label class="form-label small" for="wlDeleteAllConfirm">
                Type <code>${DELETE_ALL_PHRASE}</code> to confirm
              </label>
              <input class="form-control" id="wlDeleteAllConfirm" autocomplete="off" spellcheck="false">
              <div class="text-danger small mt-2" id="wlDeleteAllError" role="alert" hidden></div>
            </div>
            <div class="modal-footer py-2">
              <button class="btn btn-outline-secondary btn-sm" type="button" data-bs-dismiss="modal">Cancel</button>
              <button class="btn btn-danger btn-sm" type="submit" id="wlDeleteAllGo" disabled>Delete every transaction</button>
            </div>
          </form>
        </div>
      </div>
    </div>`;
}

/**
 * The most destructive thing this app can do, behind a typed phrase.
 *
 * Legacy stacks two `confirm()` dialogs, which trains the hand to click
 * through both. Typing the words is the one confirmation that cannot be done
 * by reflex, and the Delete button stays disabled until they match — so the
 * dialog never offers an action it is about to refuse. The server checks the
 * same phrase, because a disabled button is a courtesy and not a control.
 */
function wireDeleteAll(root: HTMLElement, client: WebledgerClient, onDeleted: () => void): void {
  const bar = root.querySelector<HTMLElement>('#wlDangerBar');
  if (!bar || !client.roles.includes('webledger-admin')) return;
  bar.hidden = false;

  const modalEl = root.querySelector<HTMLElement>('#wlDeleteAllModal')!;
  const form = root.querySelector<HTMLFormElement>('#wlDeleteAllForm')!;
  const input = root.querySelector<HTMLInputElement>('#wlDeleteAllConfirm')!;
  const go = root.querySelector<HTMLButtonElement>('#wlDeleteAllGo')!;
  const errorEl = root.querySelector<HTMLElement>('#wlDeleteAllError')!;

  root.querySelector('#wlDeleteAllTx')!.addEventListener('click', () => {
    input.value = '';
    go.disabled = true;
    errorEl.hidden = true;
    bsModal(modalEl)?.show();
  });
  input.addEventListener('input', () => { go.disabled = input.value.trim() !== DELETE_ALL_PHRASE; });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      go.disabled = true;
      go.textContent = 'Deleting…';
      const res = await apiFetch('/apps/webledger/transactions/delete-all', {
        method: 'POST', token: client.token, body: { confirm: input.value.trim() },
      }).catch(() => undefined);
      go.textContent = 'Delete every transaction';

      if (!res?.ok) {
        const body = res ? (await res.json().catch(() => ({}))) as { message?: string } : {};
        errorEl.hidden = false;
        errorEl.textContent = body.message ?? 'Could not reach the server.';
        go.disabled = false;
        return;
      }
      const result = (await res.json()) as { transactions: number; splits: number };
      bsModal(modalEl)?.hide();
      client.notify(
        `Deleted ${result.transactions} transaction${result.transactions === 1 ? '' : 's'} `
        + `and ${result.splits} leg${result.splits === 1 ? '' : 's'}.`,
      );
      onDeleted();
    })();
  });
}

/**
 * "By currency", plus one option per currency flagged `useForTotals`.
 *
 * Which currencies are offered is a deliberate setting rather than "all of
 * them": a picker listing 166 codes is a worse answer to "what is this worth
 * in euros" than a list of the three the book is actually kept in.
 */
// ---------------------------------------------------------------- helpers

function fillSelect(
  select: HTMLSelectElement, emptyLabel: string,
  options: Array<{ value: string; label: string }>, selected: string,
): void {
  select.innerHTML = `<option value="">${escapeHtml(emptyLabel)}</option>`
    + options.map((o) => `<option value="${escapeHtml(o.value)}">${escapeHtml(o.label)}</option>`).join('');
  select.value = selected;
}

/** The full chain, for the folder picker — two folders called "Current" are otherwise indistinguishable. */
function accountPath(accounts: readonly AccountDto[], id: string): string {
  const byId = new Map(accounts.map((a) => [a.id, a] as const));
  const chain: string[] = [];
  let node = byId.get(id);
  for (let hops = 0; node && hops < 64; hops += 1) {
    chain.unshift(node.name);
    node = node.parentId ? byId.get(node.parentId) : undefined;
  }
  return chain.join(' ➤ ');
}

/** The shared chip, with this currency's own decimals. */
/**
 * A split's account name, as a way into that account's own register — landing
 * on this very transaction, not at the top of the list.
 *
 * This is what makes a report a place you can read *from* rather than only
 * read. A card names two or three accounts and says what each one did; the
 * next question is almost always "and what else has been going on in that
 * account", and legacy answers it by making every account name a link that
 * carries the transaction with it.
 *
 * Out-of-scope legs link too. They are dimmed because they count towards
 * nothing in *this* report, which is not a reason to make them a dead end —
 * often they are exactly the leg you wanted to go and look at.
 *
 * The label is the shortened form and the title is the full path, because a
 * card showing `Assets ➤ Current ➤ Bank ➤ Revolut` four times over is a
 * column of prefixes with the answer at the end of each one.
 */
function splitAccountLink(split: ReportSplitDto, txId: string, icon: string): string {
  const href = `${appHref(`/webledger/accounts/${split.accountId}/register`)}?tx=${encodeURIComponent(txId)}`;
  const full = split.accountPath || split.accountName;
  return `<a class="wl-acct" href="${escapeHtml(href)}" `
    + `title="${escapeHtml(full)} — open and point at this transaction">`
    + `${icon}${accountLabelHtml(split.accountLabel || split.accountName)}</a>`;
}

function chip(
  amount: string, code: string, money: (c: string) => MoneyCurrency | undefined, muted = false,
): string {
  const currency = money(code);
  return currencyChipHtml(amount, code, {
    ...(currency ? { currency } : {}),
    ...(muted ? { className: 'is-muted' } : {}),
  });
}

// ---------------------------------------------------------------- all transactions

export function renderAllTransactions(
  rows: readonly ReportTransactionDto[], money: (c: string) => MoneyCurrency | undefined,
  balances: RunningBalances, iconOf: (id: string) => string = () => '',
  fromBank: ReadonlySet<string> = new Set(),
): string {
  if (rows.length === 0) return '<p class="text-secondary">No transactions.</p>';
  const { after } = balances;
  // The bar's body and its mode select are filled by `paintTotals`, which can
  // redraw them without re-rendering the report underneath.
  const totalsBar = '<div class="wl-totals" role="status">'
    + `<span class="text-secondary small">Totals · ${rows.length} transaction${rows.length === 1 ? '' : 's'}</span>`
    + '<span class="cf-cur-chips" id="wlTotalsBody"></span>'
    + '<select class="form-select form-select-sm ms-auto wl-totalmode" id="wlTotalMode" '
    + 'aria-label="Show totals by currency or converted into one"></select>'
    + '</div>';

  const cards = groupByDate(rows).map((group) => {
    const heading = `<h2 class="wl-daterule">${escapeHtml(group.date)}</h2>`;
    const body = group.transactions.map((tx) => {
      const running = after.get(tx.id) ?? {};
      const legs = tx.splits.map((split) => {
        const label = amountLabel(split.amount, split.accountType);
        const runningCell = split.inScope && running[split.currencyCode] !== undefined
          ? chip(running[split.currencyCode]!, split.currencyCode, money)
          : '';
        return `<tr${split.inScope ? '' : ' class="wl-outscope"'}>
          <td>${splitAccountLink(split, tx.id, iconOf(split.accountId))}</td>
          <td class="text-secondary">${escapeHtml(split.memo)}</td>
          <td class="wl-num"><span class="wl-dir">${escapeHtml(label)}</span>${chip(split.amount, split.currencyCode, money, !split.inScope)}</td>
          <td class="wl-num">${runningCell}</td>
        </tr>`;
      }).join('');
      return `<div class="card wl-tx-card mb-2" data-tx-card="${escapeHtml(tx.id)}">
        <span data-state-slot></span>
        <div class="card-body py-2">
          <div class="d-flex justify-content-between align-items-start gap-2">
            <div>
              <span class="fw-semibold">${escapeHtml(tx.description || '(no description)')}</span>
              ${tx.reference ? ` <span class="text-secondary small">#${escapeHtml(tx.reference)}</span>` : ''}
              ${fromBank.has(tx.id) ? bankInfoBadgeHtml(tx.id) : ''}
            </div>
            <button type="button" class="btn btn-sm btn-outline-secondary py-0" data-open-tx="${escapeHtml(tx.id)}" title="Edit this transaction">Edit</button>
          </div>
          <div class="cf-table-scroll"><table class="table table-sm mb-0 mt-1 wl-legs">
            <thead><tr><th>Account</th><th>Memo</th><th class="wl-num">Amount</th><th class="wl-num">Running</th></tr></thead>
            <tbody>${legs}</tbody>
          </table></div>
        </div>
      </div>`;
    }).join('');
    return heading + body;
  }).join('');

  return totalsBar + cards;
}

// ---------------------------------------------------------------- trial balance

export function renderTrialBalance(
  rows: readonly TrialBalanceRowDto[], money: (c: string) => MoneyCurrency | undefined,
  pathOf: (id: string) => string = () => '', iconOf: (id: string) => string = () => '',
): string {
  if (rows.length === 0) return '<p class="text-secondary">No data for this period.</p>';
  const groups = trialBalanceGroups(rows);
  // Once, above the cards — not under each. The explanation is the same for
  // every currency and repeating it seven times reads as an error banner.
  const note = groups.some((g) => g.totalDebit !== g.totalCredit) ? unbalancedNote() : '';
  return note + groups.map((group) => {
    const cur = money(group.currencyCode);
    const body = group.rows.map((row) => {
      const debit = Number(row.balance) > 0 ? formatAmount(row.balance, cur) : '';
      const credit = Number(row.balance) < 0 ? formatAmount(String(-Number(row.balance)), cur) : '';
      return `<tr>
        <td title="${escapeHtml(pathOf(row.accountId))}">${iconOf(row.accountId)}${escapeHtml(row.name)}</td>
        <td><span class="badge text-bg-light">${escapeHtml(row.type)}</span></td>
        <td class="wl-num">${escapeHtml(debit)}</td>
        <td class="wl-num">${escapeHtml(credit)}</td>
      </tr>`;
    }).join('');
    return `<div class="card mb-4">
      <div class="card-header fw-semibold">Trial balance — ${escapeHtml(group.currencyCode)}</div>
      <div class="cf-table-scroll"><table class="table table-sm mb-0">
        <thead class="table-light"><tr>
          <th>Account</th><th style="width:8rem">Type</th>
          <th class="wl-num" style="width:11rem">Debit</th>
          <th class="wl-num" style="width:11rem">Credit</th>
        </tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr class="table-light fw-semibold">
          <td colspan="2">Total</td>
          <td class="wl-num">${escapeHtml(formatAmount(group.totalDebit, cur))}</td>
          <td class="wl-num">${escapeHtml(formatAmount(group.totalCredit, cur))}</td>
        </tr></tfoot>
      </table></div>
    </div>`;
  }).join('');
}

/**
 * A trial balance is *supposed* to balance, so one that does not looks like a
 * bug — and here it routinely is not one. The report sums each leg in its own
 * account's currency with no conversion (legacy does the same), so a
 * cross-currency transaction contributes its two legs to two different
 * currency cards and neither card balances alone. Only the book as a whole
 * does, in transaction currency.
 *
 * Legacy shows the same unequal totals and says nothing, which leaves the
 * reader to either work it out or distrust the report. The numbers are
 * unchanged; this only explains them, and only when they actually differ.
 */
function unbalancedNote(): string {
  return '<div class="alert alert-secondary py-2 small">'
    + '<strong>Debits and credits do not match on every card, and that is expected here.</strong> '
    + 'This report converts nothing: each leg is counted in its own account\'s currency, so the two '
    + 'halves of a cross-currency transaction land on two different cards. The book balances in '
    + 'transaction currency, not per currency.</div>';
}

// ---------------------------------------------------------------- general ledger

export function renderGeneralLedger(
  rows: readonly GeneralLedgerRowDto[], money: (c: string) => MoneyCurrency | undefined,
  pathOf: (id: string) => string = () => '', iconOf: (id: string) => string = () => '',
): string {
  if (rows.length === 0) return '<p class="text-secondary">No data for this period.</p>';
  return generalLedgerGroups(rows).map((group) => {
    const cur = money(group.currencyCode);
    const body = group.rows.map((row) => {
      const amount = Number(row.amount);
      return `<tr>
        <td class="cf-date">${escapeHtml(row.txDate)}</td>
        <td>${escapeHtml(row.reference)}</td>
        <td>${escapeHtml(row.description)}</td>
        <td class="text-secondary">${escapeHtml(row.memo)}</td>
        <td class="wl-num">${amount > 0 ? escapeHtml(formatAmount(row.amount, cur)) : ''}</td>
        <td class="wl-num">${amount < 0 ? escapeHtml(formatAmount(String(-amount), cur)) : ''}</td>
      </tr>`;
    }).join('');
    return `<div class="card mb-4">
      <div class="card-header fw-semibold" title="${escapeHtml(pathOf(group.accountId))}">${iconOf(group.accountId)}${escapeHtml(group.accountName)}
        <span class="text-secondary">(${escapeHtml(group.currencyCode)})</span></div>
      <div class="cf-table-scroll"><table class="table table-sm mb-0">
        <thead class="table-light"><tr>
          <th style="width:7rem">Date</th><th style="width:6rem">Ref</th>
          <th>Description</th><th>Memo</th>
          <th class="wl-num" style="width:10rem">Debit</th>
          <th class="wl-num" style="width:10rem">Credit</th>
        </tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr class="table-light fw-semibold">
          <td colspan="4">Net movement</td>
          <td colspan="2" class="wl-num">${escapeHtml(formatAmount(group.net, cur))}</td>
        </tr></tfoot>
      </table></div>
    </div>`;
  }).join('');
}

// ---------------------------------------------------------------- shell

function shellBodyHtml(): string {
  return `
    <form id="wlReportFilters" class="wl-filters card card-body py-2 mb-3">
      <div class="row g-2 align-items-end">
        <div class="col-sm-3">
          <label class="form-label mb-1" for="wlReportKind">Report</label>
          <select class="form-select form-select-sm" name="report" id="wlReportKind">
            <option value="all">All transactions</option>
            <option value="trial-balance">Trial balance</option>
            <option value="general-ledger">General ledger</option>
          </select>
        </div>
        <div class="col-6 col-sm-2">
          ${notchedFieldHtml('From', dateFieldHtml('wlReportFrom', 'From', { name: 'from' }))}
        </div>
        <div class="col-6 col-sm-2">
          ${notchedFieldHtml('To', dateFieldHtml('wlReportTo', 'To', { name: 'to' }))}
        </div>
        <div class="col-sm-2" id="wlFilterCurrency">
          <label class="form-label mb-1" for="wlReportCur">Currency</label>
          <select class="form-select form-select-sm" name="currency" id="wlReportCur"><option value="">All currencies</option></select>
        </div>
        <!-- Hidden, not removed: the form reads "purpose" off its own
             elements, and a select that is not there would need every reader
             of it to learn that. The switch also empties any remembered
             value - see restoredFilters. -->
        <div class="col-sm-2" id="wlFilterPurpose"${SHOW_PURPOSES ? '' : ' hidden'}>
          <label class="form-label mb-1" for="wlReportPurpose">Purpose</label>
          <select class="form-select form-select-sm" name="purpose" id="wlReportPurpose"><option value="">All purposes</option></select>
        </div>
        <div class="col-sm-2" id="wlFilterFolder">
          <label class="form-label mb-1" for="wlReportFolder">Folder</label>
          <select class="form-select form-select-sm" name="folder" id="wlReportFolder"><option value="">Whole ledger</option></select>
        </div>
        <div class="col-sm-1"><button class="btn btn-sm btn-primary w-100" type="submit">Run</button></div>
      </div>
      <!-- Tags are chips rather than a multi-select: a <select multiple> hides
           what is chosen behind a scrollbar, and the chosen set is the thing
           you most need to see while reading the numbers it produced. -->
      <div class="row g-2 mt-1" id="wlFilterTags" hidden>
        <div class="col-12">
          <div class="d-flex align-items-baseline gap-2 flex-wrap">
            <label class="form-label mb-1" id="wlTagLabel">Tags</label>
            <span id="wlTagChips" role="group" aria-labelledby="wlTagLabel"></span>
            <span class="ms-auto d-flex align-items-center gap-1" id="wlTagModeWrap" hidden>
              <label class="form-label mb-0 small text-secondary" for="wlTagMode">Match</label>
              <select class="form-select form-select-sm w-auto" name="tagMode" id="wlTagMode">
                <option value="any">any of them</option>
                <option value="all">all of them</option>
              </select>
            </span>
          </div>
          <div class="form-text" id="wlTagHelp" hidden>
            Totals count the expense and income legs of the matching transactions; the funding side is
            greyed, because a whole transaction balances to zero.
          </div>
        </div>
      </div>
    </form>
    <!-- Administrators only, revealed once the roles are known. -->
    <div class="d-flex justify-content-end mb-2" id="wlDangerBar" hidden>
      <button class="btn btn-sm btn-outline-danger" type="button" id="wlDeleteAllTx">Delete all transactions…</button>
    </div>
    ${deleteAllModalHtml()}
    <div class="alert alert-warning py-2 small" id="wlReportStale" role="status" hidden></div>
    <div id="wlReportArea"></div>`;
}
